import {
  AddressInput,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icons,
  Input,
  InputErrorMessage,
  Label,
  MultiSelect,
  RadioGroup,
  RadioGroupItem,
  SingleCombobox,
  Textarea,
} from '@commenda/kakashi';
import { IAddress } from '@commenda/types';
import { OptionType } from '@commenda/utils';
import { Country } from '@prisma/client';
import { useState } from 'react';

import {
  UKExemptSuppliesAnswer,
  UKPresenceType,
  UKVATRegistrationReason,
} from '../../../../indirect-tax/registrations/wizards/new-state/constants';
import { IndirectTaxRegistrationHelper } from '../../../../indirect-tax/registrations/utils/IndirectTaxRegistrationHelper';
import { IndirectTaxRegistrationValue } from './types';

type UKActivity = NonNullable<IndirectTaxRegistrationValue['ukActivity']>;

const EMPTY_PROVIDER: { name: string; address: IAddress } = {
  name: '',
  address: {
    addressLine1: '',
    addressLine2: '',
    postalCode: '',
    city: '',
    state: '',
    country: Country.GB,
  },
};

export function validateUKActivity(
  ukActivity: UKActivity | undefined
): string | null {
  const value = ukActivity ?? {};
  if (!value.ukVATRegistrationReasons?.length) {
    return 'Pick at least one reason for UK VAT registration';
  }
  if (!value.ukActivityDescription?.trim()) {
    return 'Describe the activities you will carry out in the UK';
  }
  if (value.goodsStoredOrShippedFromUK == null) {
    return 'Select whether goods will be stored or shipped from the UK';
  }
  if (value.goodsStoredOrShippedFromUK === true) {
    const providers = value.ukGoodProviders ?? [];
    if (providers.length < 1) {
      return 'Add at least one goods provider';
    }
    const hasIncompleteProvider = providers.some(
      (provider) =>
        !provider.name.trim() ||
        !provider.address.addressLine1.trim() ||
        !provider.address.city.trim() ||
        !provider.address.postalCode.trim() ||
        !provider.address.country.trim()
    );
    if (hasIncompleteProvider) {
      return 'Complete every provider name and address';
    }
  }
  if (!value.hasUKExemptSupplies) {
    return 'Select whether you will make exempt supplies in the UK';
  }
  if (!value.ukPresenceTypes?.length) {
    return 'Pick at least one UK presence option';
  }
  const hasNonNonePresence = value.ukPresenceTypes.some(
    (presence) => presence !== UKPresenceType.NONE
  );
  if (hasNonNonePresence && !value.ukPresenceDetails?.trim()) {
    return 'Tell us about your UK presence';
  }
  if (value.directorsInvolvedInOtherUKBusiness == null) {
    return 'Select whether directors are involved in another UK or Isle of Man business';
  }
  if (
    value.directorsInvolvedInOtherUKBusiness === true &&
    !value.otherUKBusinessesDetails?.trim()
  ) {
    return 'List the other businesses';
  }
  return null;
}

type ProviderModalState = { mode: 'add' } | { mode: 'edit'; index: number } | null;

export const UKActivityAndPresenceSection = ({
  value,
  onChange,
}: {
  value: UKActivity | undefined;
  onChange: (patch: Partial<UKActivity>) => void;
}) => {
  const ukVATRegistrationReasons = value?.ukVATRegistrationReasons ?? [];
  const ukActivityDescription = value?.ukActivityDescription ?? '';
  const goodsStoredOrShippedFromUK = value?.goodsStoredOrShippedFromUK;
  const ukGoodProviders = value?.ukGoodProviders ?? [];
  const hasUKExemptSupplies = value?.hasUKExemptSupplies;
  const ukPresenceTypes = value?.ukPresenceTypes ?? [];
  const ukPresenceDetails = value?.ukPresenceDetails ?? '';
  const directorsInvolvedInOtherUKBusiness =
    value?.directorsInvolvedInOtherUKBusiness;
  const otherUKBusinessesDetails = value?.otherUKBusinessesDetails ?? '';

  const hasNonNonePresence = ukPresenceTypes.some(
    (presence) => presence !== UKPresenceType.NONE
  );

  const handleUKPresenceTypesChange = (nextValues: string[]) => {
    const wasNoneSelected = ukPresenceTypes.includes(UKPresenceType.NONE);
    const isNoneSelectedNow = nextValues.includes(UKPresenceType.NONE);

    let sanitizedValues = nextValues;
    if (!wasNoneSelected && isNoneSelectedNow) {
      sanitizedValues = [UKPresenceType.NONE];
    } else if (
      wasNoneSelected &&
      isNoneSelectedNow &&
      nextValues.length > ukPresenceTypes.length
    ) {
      sanitizedValues = nextValues.filter((v) => v !== UKPresenceType.NONE);
    }

    onChange({ ukPresenceTypes: sanitizedValues });
  };

  const [providerModal, setProviderModal] = useState<ProviderModalState>(null);
  const [draftProvider, setDraftProvider] = useState(EMPTY_PROVIDER);
  const [draftErrors, setDraftErrors] = useState<{
    name?: string;
    address?: Partial<Record<keyof IAddress, string>>;
  }>({});

  const removeProvider = (i: number) => {
    onChange({
      ukGoodProviders: ukGoodProviders.filter((_, idx) => idx !== i),
    });
  };

  const openAddProvider = () => {
    setDraftProvider(EMPTY_PROVIDER);
    setDraftErrors({});
    setProviderModal({ mode: 'add' });
  };

  const openEditProvider = (i: number) => {
    const source = ukGoodProviders[i];
    setDraftProvider({
      name: source.name ?? '',
      address: {
        addressLine1: source.address.addressLine1 ?? '',
        addressLine2: source.address.addressLine2 ?? '',
        postalCode: source.address.postalCode ?? '',
        city: source.address.city ?? '',
        state: source.address.state ?? '',
        country: source.address.country ?? '',
      },
    });
    setDraftErrors({});
    setProviderModal({ mode: 'edit', index: i });
  };

  const closeProviderModal = () => setProviderModal(null);

  const validateDraft = (): boolean => {
    const errors: typeof draftErrors = {};
    if (!draftProvider.name.trim()) {
      errors.name = 'Provider name is required';
    }
    const addr = draftProvider.address;
    const addressErrors: NonNullable<typeof draftErrors.address> = {};
    if (!addr.addressLine1.trim()) {
      addressErrors.addressLine1 = 'Address line 1 is required';
    }
    if (!addr.city.trim()) {
      addressErrors.city = 'City is required';
    }
    if (!addr.postalCode.trim()) {
      addressErrors.postalCode = 'Postcode is required';
    }
    if (!addr.country.trim()) {
      addressErrors.country = 'Country is required';
    }
    if (Object.keys(addressErrors).length) {
      errors.address = addressErrors;
    }
    setDraftErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveDraftProvider = () => {
    if (!validateDraft() || !providerModal) return;
    const next =
      providerModal.mode === 'add'
        ? [...ukGoodProviders, draftProvider]
        : ukGoodProviders.map((p, idx) =>
            idx === providerModal.index ? draftProvider : p
          );
    onChange({ ukGoodProviders: next });
    closeProviderModal();
  };

  return (
    <div className="flex flex-col gap-4xl">
      <div className="flex flex-col gap-xxs">
        <p className="txt-medium-regular text-text-tertiary">
          Help HMRC understand why you&apos;re registering, what you do in the
          UK, and any UK presence you have.
        </p>
      </div>

      <div className="flex flex-col gap-xl">
        <div className="flex flex-col gap-xxs">
          <p className="txt-medium-bold text-text-primary">
            Reason for UK VAT registration
            <span className="txt-small-medium text-utility-violet-600 ml-0.5">
              *
            </span>
          </p>
          <p className="txt-medium-regular text-text-tertiary">
            Why you&apos;re registering — select all that apply.
          </p>
        </div>
        <MultiSelect
          id="uk-vat-registration-reasons"
          options={
            IndirectTaxRegistrationHelper.UK_VAT_REGISTRATION_REASON_OPTIONS
          }
          defaultValue={ukVATRegistrationReasons}
          onValueChange={(nextValues) =>
            onChange({
              ukVATRegistrationReasons: nextValues as UKVATRegistrationReason[],
            })
          }
          placeholder="Select reason(s)"
        />
      </div>

      <div className="flex flex-col gap-xl">
        <div className="flex flex-col gap-xxs">
          <p className="txt-medium-bold text-text-primary">
            Describe the activities you&apos;ll carry out in the UK
            <span className="txt-small-medium text-utility-violet-600 ml-0.5">
              *
            </span>
          </p>
          <p className="txt-medium-regular text-text-tertiary">
            Be specific — e.g. plasterer, management consultancy, art retailer.
          </p>
        </div>
        <Textarea
          value={ukActivityDescription}
          onChange={(e) =>
            onChange({ ukActivityDescription: e.target.value })
          }
          placeholder="2–3 sentences describing your UK activity"
          className="txt-medium-regular text-text-secondary px-3"
        />
      </div>

      <div className="flex flex-col gap-xl">
        <div className="flex flex-col gap-xxs">
          <p className="txt-medium-bold text-text-primary">
            Will goods be stored or shipped from the UK?
            <span className="txt-small-medium text-utility-violet-600 ml-0.5">
              *
            </span>
          </p>
          <p className="txt-medium-regular text-text-tertiary">
            Includes any stock held in UK warehouses, including Amazon FBA and
            other 3PLs.
          </p>
        </div>
        <RadioGroup
          value={
            goodsStoredOrShippedFromUK === true
              ? OptionType.YES
              : goodsStoredOrShippedFromUK === false
                ? OptionType.NO
                : undefined
          }
          onValueChange={(v) =>
            onChange({
              goodsStoredOrShippedFromUK: v === OptionType.YES,
              ...(v === OptionType.NO ? { ukGoodProviders: [] } : {}),
            })
          }
          className="flex flex-row items-center gap-xl"
        >
          <div className="flex items-center gap-xs">
            <RadioGroupItem
              value={OptionType.YES}
              id="goods-stored-uk-yes"
              className="bg-white"
            />
            <Label htmlFor="goods-stored-uk-yes">Yes</Label>
          </div>
          <div className="flex items-center gap-xs">
            <RadioGroupItem
              value={OptionType.NO}
              id="goods-stored-uk-no"
              className="bg-white"
            />
            <Label htmlFor="goods-stored-uk-no">No</Label>
          </div>
        </RadioGroup>
      </div>

      {goodsStoredOrShippedFromUK === true && (
        <div className="flex flex-col gap-xl">
          <div className="flex flex-col gap-xxs">
            <p className="txt-medium-bold text-text-primary">
              Where are the goods stored, and who fulfills them?
              <span className="txt-small-medium text-utility-violet-600 ml-0.5">
                *
              </span>
            </p>
            <p className="txt-medium-regular text-text-tertiary">
              Provider name and warehouse address — e.g. Amazon FBA, East
              Midlands EMA1. Add one card per provider.
            </p>
          </div>
          {ukGoodProviders.map((provider, i) => (
            <div
              key={`${provider.name}-${provider.address.addressLine1}`}
              className="p-md border rounded-md border-border-primary bg-white"
            >
              <div className="flex justify-between items-center">
                <div className="flex gap-md items-center">
                  <Icons.Building02 className="text-fg-quinary h-5 w-5" />
                  <h4 className="text-text-primary txt-small-medium">
                    {provider.name || `Provider ${i + 1}`}
                  </h4>
                </div>
                <div className="flex gap-xxs items-center">
                  <div
                    className="p-sm hover:bg-bg-secondary-hover rounded-sm cursor-pointer"
                    onClick={() => openEditProvider(i)}
                    data-testid="uk-good-provider-edit"
                  >
                    <Icons.Pencil02 className="h-5 w-5 text-button-tertiary-fg hover:text-button-tertiary-fg-hover" />
                  </div>
                  <div
                    className="p-sm hover:bg-bg-secondary-hover rounded-sm cursor-pointer"
                    onClick={() => removeProvider(i)}
                    data-testid="uk-good-provider-remove"
                  >
                    <Icons.XClose className="h-5 w-5 text-button-tertiary-fg hover:text-button-tertiary-fg-hover" />
                  </div>
                </div>
              </div>
            </div>
          ))}
          <div>
            <Button type="button" size="sm" onClick={openAddProvider}>
              Add provider
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-xl">
        <div className="flex flex-col gap-xxs">
          <p className="txt-medium-bold text-text-primary">
            Will you make any exempt supplies in the UK?
            <span className="txt-small-medium text-utility-violet-600 ml-0.5">
              *
            </span>
          </p>
          <p className="txt-medium-regular text-text-tertiary">
            Sales not subject to VAT — e.g. financial services, insurance,
            education, residential rent.
          </p>
        </div>
        <SingleCombobox
          id="uk-exempt-supplies"
          value={hasUKExemptSupplies ?? ''}
          options={
            IndirectTaxRegistrationHelper.UK_EXEMPT_SUPPLIES_ANSWER_OPTIONS
          }
          setValue={(nextValue) =>
            onChange({
              hasUKExemptSupplies: nextValue as UKExemptSuppliesAnswer,
            })
          }
          placeholder="Select an answer"
        />
      </div>

      <div className="flex flex-col gap-xl">
        <div className="flex flex-col gap-xxs">
          <p className="txt-medium-bold text-text-primary">
            Do you have a warehouse, representative, or staff in the UK?
            <span className="txt-small-medium text-utility-violet-600 ml-0.5">
              *
            </span>
          </p>
          <p className="txt-medium-regular text-text-tertiary">
            Tick all that apply, or None if you have no UK presence beyond
            selling.
          </p>
        </div>
        <MultiSelect
          id="uk-presence-types"
          options={IndirectTaxRegistrationHelper.UK_PRESENCE_TYPE_OPTIONS}
          defaultValue={ukPresenceTypes}
          onValueChange={handleUKPresenceTypesChange}
          placeholder="Select option(s)"
          hideSelectAll
        />
      </div>

      {hasNonNonePresence && (
        <div className="flex flex-col gap-xl">
          <div className="flex flex-col gap-xxs">
            <p className="txt-medium-bold text-text-primary">
              Tell us about your UK presence
              <span className="txt-small-medium text-utility-violet-600 ml-0.5">
                *
              </span>
            </p>
            <p className="txt-medium-regular text-text-tertiary">
              Address, name, and role for each — so we can verify your
              establishment status.
            </p>
          </div>
          <Textarea
            value={ukPresenceDetails}
            onChange={(e) => onChange({ ukPresenceDetails: e.target.value })}
            placeholder="Provide details for each warehouse, representative, or staff member"
            className="txt-medium-regular text-text-secondary px-3"
          />
        </div>
      )}

      <div className="flex flex-col gap-xl">
        <div className="flex flex-col gap-xxs">
          <p className="txt-medium-bold text-text-primary">
            Are any directors or owners currently involved — or in the last 2
            years — in another UK or Isle of Man business?
            <span className="txt-small-medium text-utility-violet-600 ml-0.5">
              *
            </span>
          </p>
          <p className="txt-medium-regular text-text-tertiary">
            HMRC requires this even when the answer is No (VAT1 Q21).
          </p>
        </div>
        <RadioGroup
          value={
            directorsInvolvedInOtherUKBusiness === true
              ? OptionType.YES
              : directorsInvolvedInOtherUKBusiness === false
                ? OptionType.NO
                : undefined
          }
          onValueChange={(v) =>
            onChange({
              directorsInvolvedInOtherUKBusiness: v === OptionType.YES,
              ...(v === OptionType.NO
                ? { otherUKBusinessesDetails: '' }
                : {}),
            })
          }
          className="flex flex-row items-center gap-xl"
        >
          <div className="flex items-center gap-xs">
            <RadioGroupItem
              value={OptionType.YES}
              id="directors-other-uk-yes"
              className="bg-white"
            />
            <Label htmlFor="directors-other-uk-yes">Yes</Label>
          </div>
          <div className="flex items-center gap-xs">
            <RadioGroupItem
              value={OptionType.NO}
              id="directors-other-uk-no"
              className="bg-white"
            />
            <Label htmlFor="directors-other-uk-no">No</Label>
          </div>
        </RadioGroup>
      </div>

      {directorsInvolvedInOtherUKBusiness === true && (
        <div className="flex flex-col gap-xl">
          <div className="flex flex-col gap-xxs">
            <p className="txt-medium-bold text-text-primary">
              List those other businesses
              <span className="txt-small-medium text-utility-violet-600 ml-0.5">
                *
              </span>
            </p>
            <p className="txt-medium-regular text-text-tertiary">
              Name, role, VAT number or other HMRC tax reference for each.
            </p>
          </div>
          <Textarea
            value={otherUKBusinessesDetails}
            onChange={(e) =>
              onChange({ otherUKBusinessesDetails: e.target.value })
            }
            placeholder="One business per line"
            className="txt-medium-regular text-text-secondary px-3"
          />
        </div>
      )}

      <Dialog
        open={providerModal !== null}
        onOpenChange={(open) => {
          if (!open) closeProviderModal();
        }}
      >
        <DialogContent className="max-w-[640px] flex flex-col">
          <DialogHeader className="flex-col items-start">
            <DialogTitle>
              {providerModal?.mode === 'edit' ? 'Edit provider' : 'Add provider'}
            </DialogTitle>
            <DialogDescription>
              Provider name and warehouse address — e.g. Amazon FBA, East
              Midlands EMA1.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-xl p-6">
            <div className="flex flex-col gap-xs">
              <Label>
                Provider name
                <span className="text-bg-fg-secondary">*</span>
              </Label>
              <Input
                value={draftProvider.name}
                onChange={(e) =>
                  setDraftProvider({ ...draftProvider, name: e.target.value })
                }
                placeholder="Provider name (e.g. Amazon FBA)"
                fullWidth
                showErrorMessage={false}
                error={draftErrors.name}
              />
              {draftErrors.name && (
                <InputErrorMessage message={draftErrors.name} />
              )}
            </div>

            <AddressInput
              id="uk-goods-storage-address-modal"
              address={draftProvider.address}
              onChange={(nextAddress) =>
                setDraftProvider({ ...draftProvider, address: nextAddress })
              }
              defaultCountry={Country.GB}
              error={draftErrors.address}
              disabled={{ country: true }}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={closeProviderModal}
            >
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={saveDraftProvider}>
              {providerModal?.mode === 'edit' ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
